// KHIYARSA PATCH: checklist interaction geometry. Clicking a checklist
// item's checkbox ("☐ "/"☑ ", legacy "✓ ") on canvas toggles its completed
// state — this module answers "which item's checkbox sits under this scene
// point?", using the exact same per-line direction/alignment rules the
// renderers apply. Plain bullets ("• "/"◦ ") are NOT hit — they aren't
// checklist items.
import { getFontString, isRTL } from "@excalidraw/common";
import { pointFrom, pointRotateRads } from "@excalidraw/math";

import type { Radians } from "@excalidraw/math";

import {
  getRenderedListLineStates,
  hasListLine,
  isChecklistMarker,
  matchBulletLine,
} from "./bulletText";
import { getLineHeightInPx, getLineWidth } from "./textMeasurements";

import type { ExcalidrawTextElement, TextAlign } from "./types";

/**
 * Whether per-line automatic direction alignment applies: default
 * left-aligned text that contains list lines gets Claude-style per-item
 * alignment — Arabic items right-aligned RTL, English items left-aligned
 * LTR. Explicit center/right alignment is respected as-is.
 */
export const isAutoListAlign = (textAlign: TextAlign | string, text: string) =>
  textAlign === "left" && hasListLine(text);

/** x of the line's visual start edge inside the element box */
export const getListLineStartX = (
  effectiveAlign: TextAlign | string,
  elementWidth: number,
  lineWidth: number,
) =>
  effectiveAlign === "center"
    ? (elementWidth - lineWidth) / 2
    : effectiveAlign === "right"
    ? elementWidth - lineWidth
    : 0;

/** generous horizontal slop around the marker hit zone, in px */
const MARKER_HIT_PADDING = 4;

/**
 * Hit-test a scene point against the checklist markers of a text element.
 * Returns the 0-based HARD (originalText) line index of the hit item, or
 * null. Handles rotation, word-wrapped text, and RTL items (marker on the
 * right edge).
 */
export const getChecklistHitAt = (
  element: ExcalidrawTextElement,
  sceneX: number,
  sceneY: number,
): number | null => {
  if (!hasListLine(element.originalText)) {
    return null;
  }

  const centerX = element.x + element.width / 2;
  const centerY = element.y + element.height / 2;
  const [unrotatedX, unrotatedY] = pointRotateRads(
    pointFrom(sceneX, sceneY),
    pointFrom(centerX, centerY),
    -element.angle as Radians,
  );
  const localX = unrotatedX - element.x;
  const localY = unrotatedY - element.y;

  if (
    localX < -MARKER_HIT_PADDING ||
    localX > element.width + MARKER_HIT_PADDING ||
    localY < 0 ||
    localY > element.height
  ) {
    return null;
  }

  const renderedLines = element.text.replace(/\r\n?/g, "\n").split("\n");
  const lineHeightPx = getLineHeightInPx(element.fontSize, element.lineHeight);
  const lineIndex = Math.floor(localY / lineHeightPx);
  if (lineIndex < 0 || lineIndex >= renderedLines.length) {
    return null;
  }

  const states = getRenderedListLineStates(
    element.originalText,
    renderedLines,
    isRTL,
  );
  const state = states[lineIndex];
  if (!state?.hasMarker) {
    return null;
  }

  const originalLine =
    element.originalText.split("\n")[state.originalLineIndex];
  const match = matchBulletLine(originalLine);
  if (!match || !isChecklistMarker(match.marker)) {
    return null;
  }

  const font = getFontString(element);
  const renderedLine = renderedLines[lineIndex];
  const lineWidth = getLineWidth(renderedLine, font);
  const prefixWidth = getLineWidth(`${match.indent}${match.marker} `, font);

  const autoAlign = isAutoListAlign(element.textAlign, element.originalText);
  const effectiveAlign = autoAlign
    ? state.rtl
      ? "right"
      : "left"
    : element.textAlign;
  const lineStartX = getListLineStartX(
    effectiveAlign,
    element.width,
    lineWidth,
  );

  // in RTL the marker sits at the line's visual RIGHT end
  const [zoneStart, zoneEnd] = state.rtl
    ? [lineStartX + lineWidth - prefixWidth, lineStartX + lineWidth]
    : [lineStartX, lineStartX + prefixWidth];

  if (
    localX >= zoneStart - MARKER_HIT_PADDING &&
    localX <= zoneEnd + MARKER_HIT_PADDING
  ) {
    return state.originalLineIndex;
  }
  return null;
};
